import { useState } from 'react';
import { useGame } from '../state/SocketProvider';
import { hasPrefs, rememberSettings, resetPrefs } from '../state/settingsPrefsStore';
import type { WinCondition } from '../../shared/series';
import {
  FOUND_RADIUS_FT_MAX,
  FOUND_RADIUS_FT_MIN,
  FOUND_RADIUS_FT_STEP,
  VETO_STEP_MS,
} from '../../shared/constants'; // v22 BOLT-113, v29 BOLT-163

const CONDITIONS: { id: WinCondition; label: string }[] = [
  { id: 'best', label: 'Best round' },
  { id: 'cumulative', label: 'Cumulative' },
  { id: 'mostRounds', label: 'Most rounds' },
];

/** Host game settings (v4): Quick Setup (rounds per team, win condition) always
 *  shown; Advanced (the veto window + round cap) behind a disclosure (§3 / §8). */
export function GameSettings() {
  const { state, send } = useGame();
  const [advanced, setAdvanced] = useState(false);
  // v29 BOLT-142: whether anything is stored for this device. Read once on
  // mount - it must never be a live input to what the room is doing.
  const [remembered, setRemembered] = useState(() => hasPrefs());
  if (!state) return null;
  const s = state.settings;
  // v29 BOLT-142: every host edit is remembered for the NEXT game they create.
  // Writing here (rather than reading anywhere) is the whole design - see
  // shared/settingsPrefs.ts.
  const set = (settings: Partial<typeof s>) => {
    rememberSettings(settings);
    setRemembered(true);
    send({ t: 'set_settings', settings });
  };

  const Stepper = ({
    label,
    value,
    suffix,
    onMinus,
    onPlus,
    testid,
  }: {
    label: string;
    value: string;
    suffix?: string;
    onMinus: () => void;
    onPlus: () => void;
    testid: string;
  }) => (
    <div className="gs-row">
      <span className="gs-label">{label}</span>
      <div className="gs-stepper">
        <button className="gs-step" onClick={onMinus} data-testid={`${testid}-minus`}>
          −
        </button>
        <span className="gs-value" data-testid={testid}>
          {value}
          {suffix && <span className="gs-suffix">{suffix}</span>}
        </span>
        <button className="gs-step" onClick={onPlus} data-testid={`${testid}-plus`}>
          +
        </button>
      </div>
    </div>
  );

  return (
    <div className="panel section-gap game-settings" data-testid="game-settings">
      <div className="field__label">Game settings</div>

      <Stepper
        label="Rounds per team"
        value={String(s.roundsPerTeam)}
        testid="gs-rounds"
        onMinus={() => set({ roundsPerTeam: Math.max(1, s.roundsPerTeam - 1) })}
        onPlus={() => set({ roundsPerTeam: Math.min(5, s.roundsPerTeam + 1) })}
      />

      <div className="gs-row">
        <span className="gs-label">Win condition</span>
        <div className="gs-conditions">
          {CONDITIONS.map((c) => (
            <button
              key={c.id}
              className={`gs-chip${s.winCondition === c.id ? ' gs-chip--on' : ''}`}
              data-testid={`gs-win-${c.id}`}
              data-active={s.winCondition === c.id}
              onClick={() => set({ winCondition: c.id })}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <button className="gs-disclose" data-testid="gs-advanced-toggle" onClick={() => setAdvanced((a) => !a)}>
        {advanced ? '▾ Advanced' : '▸ Advanced'}
      </button>
      {advanced && (
        <div className="gs-advanced" data-testid="gs-advanced">
          {/* v18 BOLT-081: ONE length for every timed hider card (Freeze,
              Silence, Babel, Flying Blind) — three settings rows collapsed
              into this. Minutes, because that is how Jake said it. */}
          <Stepper
            label="Card block length"
            value={String(Math.round(s.blockDurationMs / 60_000))}
            suffix="m"
            testid="gs-block"
            onMinus={() => set({ blockDurationMs: Math.max(15_000, s.blockDurationMs - 60_000) })}
            onPlus={() => set({ blockDurationMs: Math.min(10 * 60_000, s.blockDurationMs + 60_000) })}
          />
          <Stepper
            label="Veto window"
            value={String(Math.round(s.vetoWindowMs / 1000))}
            suffix="s"
            testid="gs-veto"
            onMinus={() => set({ vetoWindowMs: Math.max(0, s.vetoWindowMs - VETO_STEP_MS) })}
            onPlus={() => set({ vetoWindowMs: Math.min(5 * 60_000, s.vetoWindowMs + VETO_STEP_MS) })}
          />
          <Stepper
            label="Max round"
            value={String(Math.round(s.maxRoundMs / 60_000))}
            suffix="m"
            testid="gs-maxround"
            onMinus={() => set({ maxRoundMs: Math.max(60_000, s.maxRoundMs - 5 * 60_000) })}
            onPlus={() => set({ maxRoundMs: Math.min(90 * 60_000, s.maxRoundMs + 5 * 60_000) })}
          />
          {/* v22 BOLT-113: how close a seeker must get for Found to succeed.
              It was ALWAYS a real wire setting, just never surfaced, so from
              the player's seat it read as hard-coded. It is a DISTANCE, not a
              duration, so it is the one stepper here measured in feet. */}
          <Stepper
            label="Found distance"
            value={String(s.foundRadiusFt)}
            suffix="ft"
            testid="gs-found"
            onMinus={() =>
              set({ foundRadiusFt: Math.max(FOUND_RADIUS_FT_MIN, s.foundRadiusFt - FOUND_RADIUS_FT_STEP) })
            }
            onPlus={() =>
              set({ foundRadiusFt: Math.min(FOUND_RADIUS_FT_MAX, s.foundRadiusFt + FOUND_RADIUS_FT_STEP) })
            }
          />
          {/* v29 BOLT-142 acceptance #7/#8: what is remembered must be VISIBLE,
              and there must be a way back. A wrong sticky setting with no exit
              is worse than no stickiness at all. */}
          {remembered && (
            <div className="gs-remembered" data-testid="gs-remembered">
              <span className="eyebrow">These settings carry into your next game</span>
              <button
                className="btn btn--ghost btn--sm"
                data-testid="gs-reset-prefs"
                onClick={() => {
                  resetPrefs();
                  setRemembered(false);
                }}
              >
                Reset to defaults
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
